<!-- identity-groups.component.html -->
<app-page-layout
  [pageTitle]="'User Groups'"
  [subtitle]="'Manage User Groups to organize users and service identities for policy assignment.'">

  <!-- Actions Bar -->
  <div page-actions class="flex items-center gap-2">
    <p-toast/>
    <p-button
      label="Create User Group"
      icon="pi pi-plus"
      (onClick)="openCreateGroupDialog()"
      severity="primary"
      size="small"
      outlined
    ></p-button>
  </div>

  <p-confirmDialog></p-confirmDialog>
  
  <!-- State Container -->
  <app-state-container
    [state]="viewState()"
    loadingMessage="Loading user groups..."
    loadingSubMessage="Please wait while we fetch your groups"
    emptyIcon="pi-users"
    emptyTitle="No user groups found"
    [emptyMessage]="emptyStateMessage()"
    [showEmptyAction]="true"
    emptyActionLabel="Create User Group"
    emptyActionIcon="pi-plus"
    [emptyActionOutlined]="true"
    [showClearFilters]="showClearFilters()"
    [errorMessage]="errorMessage()"
    (emptyAction)="openCreateGroupDialog()"
    (clearFilters)="clearFilters()"
    (retry)="retryLoad()"
  >
    <!-- Groups Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
      @for (group of filteredGroups(); track group.id) {
        <div class="bg-white dark:bg-zinc-950 rounded-lg border border-surface-200 dark:border-zinc-800 p-5 hover:shadow-md transition-shadow">
          <!-- Header -->
          <div class="flex justify-between items-start mb-3">
            <h3 class="text-base font-semibold text-surface-900 dark:text-zinc-100">{{ group.name }}</h3>
            <p-tag [value]="group.type"></p-tag>
          </div>

          <!-- Members Count -->
          <div class="text-sm text-surface-600 dark:text-surface-400 mb-3">
            <i class="pi pi-users mr-2"></i>{{ group.members }} members
          </div>

          <!-- Meta Tags -->
          <div class="flex flex-wrap gap-2 mb-3">
            <p-tag severity="info" [value]="group.rule"></p-tag>
            <p-tag severity="success" [value]="authDisplayMap.get(group.auth) || group.auth"></p-tag>
          </div>

          <div class="mb-3 flex min-w-0 items-center gap-1 overflow-hidden">
            <div class="flex min-w-0 shrink gap-1 overflow-hidden">
              @for (scope of getVisibleScopes(group.scope); track scope) {
                <span class="inline-flex min-w-0 max-w-32 shrink-0 truncate rounded-full bg-primary-100 px-2.5 py-1 text-[10px] font-medium text-primary-700 dark:bg-primary-500/20 dark:text-primary-300">
                  {{ scope }}
                </span>
              }
            </div>
            @if (getRemainingScopesCount(group.scope) > 0) {
              <span class="inline-flex shrink-0 cursor-help rounded-full bg-surface-100 px-2.5 py-1 text-[10px] font-medium text-surface-700 dark:bg-surface-500/20 dark:text-surface-300"
                    [pTooltip]="getRemainingScopesTooltip(group.scope)"
                    tooltipPosition="top"
                    tooltipStyleClass="scopes-tooltip">
                +{{ getRemainingScopesCount(group.scope) }}
              </span>
            }
            @if (!group.scope || group.scope.length === 0) {
              <span class="text-xs text-surface-400 dark:text-surface-500">
                No scopes assigned
              </span>
            }
          </div>

          <!-- Policy -->
          <p class="text-sm text-surface-700 dark:text-surface-300 mb-4">
            <strong>Policy:</strong> {{ group.policy }}
          </p>

          <!-- Actions -->
          <div class="flex gap-2">
            <p-button
              label="Configure"
              icon="pi pi-cog"
              severity="secondary"
              size="small"
              outlined
              (onClick)="configureGroup(group)"
            ></p-button>
            <p-button
              label="Manage Members"
              icon="pi pi-users"
              severity="primary"
              size="small"
              outlined
              (onClick)="manageMembers(group)"
            ></p-button>
          </div>
        </div>
      }
    </div>
  </app-state-container>

  <!-- Create/Edit Dialog -->
  <p-dialog
    [(visible)]="showDialog"
    [modal]="true"
    [closable]="true"
    [style]="{ width: '550px' }"
    [header]="dialogMode === 'create' ? 'Create User Group' : 'Configure User Group'"
    [draggable]="false"
    [resizable]="false"
  >
    <form [formGroup]="groupForm" class="space-y-4" autocomplete="off">
      <!-- Group Name -->
      <div>
        <label class="block text-sm font-medium mb-1">Group Name <span class="text-red-500">*</span></label>
        <input pInputText formControlName="name" placeholder="e.g., Dealers"
               class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"/>
        @if (groupForm.get('name')?.touched && groupForm.get('name')?.invalid) {
          <small class="text-red-500">Group name is required</small>
        }
      </div>

      <!-- Authentication Requirement -->
      <div>
        <label class="block text-sm font-medium mb-1">Authentication Requirement <span class="text-red-500">*</span></label>
        <p-select
          formControlName="auth"
          [options]="authOptions"
          optionLabel="label"
          appendTo="body"
          optionValue="value"
          class="w-full"
          placeholder="Select authentication method"
          [class.ng-invalid]="groupForm.get('auth')?.invalid && groupForm.get('auth')?.touched"
        ></p-select>
        @if (groupForm.get('auth')?.touched && groupForm.get('auth')?.invalid) {
          <small class="text-red-500">Authentication requirement is required</small>
        }
      </div>

      <!-- Policy Baseline Dropdown -->
      <div>
        <label class="block text-sm font-medium mb-1">Policy Baseline</label>
        <p-select
          formControlName="policyId"
          [options]="policyOptions"
          class="w-full"
          appendTo="body"
          placeholder="Select policy baseline"
          (onChange)="onPolicyChange()"
          [loading]="isLoadingPolicies"
        >
          <ng-template let-item>
            {{ item.label }}
          </ng-template>
        </p-select>
      </div>

      <!-- Default Allowed Scopes -->
      <div>
        <label class="block text-sm font-medium mb-1">Default Allowed Scopes</label>
        <div class="min-h-[42px] rounded-lg border border-surface-200 bg-surface-50 p-3 dark:border-surface-700 dark:bg-surface-800">
          <div class="flex flex-wrap gap-2">
            @for (scope of policyScopeMapping.get(groupForm.get('policyId')?.value) || []; track scope) {
              <span class="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 dark:bg-primary-500/20 dark:text-primary-300">
                {{ scope }}
              </span>
            }
            @if ((policyScopeMapping.get(groupForm.get('policyId')?.value) || []).length === 0) {
              <span class="text-sm text-surface-400 dark:text-surface-500">
                Choose a policy to see scopes
              </span>
            }
          </div>
        </div>
      </div>
    </form>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <p-button
          label="Cancel"
          severity="secondary"
          (onClick)="closeDialog()"
        ></p-button>
        <p-button
          label="{{ dialogMode === 'create' ? 'Create' : 'Save' }}"
          severity="primary"
          [disabled]="groupForm.invalid || isSubmitting"
          [loading]="isSubmitting"
          (onClick)="saveGroup()"
        ></p-button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Drawer Container -->
  <ng-container #drawerContainer></ng-container>
</app-page-layout>